import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { convertDocxToHtml } from '../../../../utils/docxToHtml';
import { sanitizeDocumentHtml } from '../../../../utils/sanitizeDocumentHtml';
import { applyDocxLayoutFixes } from '../../../../utils/docxLayoutFix';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { DocumentTemplateCategory } from '../../../../api/General/documentTemplates';

const CATEGORY_OPTIONS = [
  { value: DocumentTemplateCategory.Contract, label: 'Contract' },
  { value: DocumentTemplateCategory.Certificate, label: 'Certificate' },
  { value: DocumentTemplateCategory.Letter, label: 'Letter' },
  { value: DocumentTemplateCategory.Settlement, label: 'Settlement' },
  { value: DocumentTemplateCategory.Other, label: 'Other' },
];

const MAX_FILE_BYTES = 10 * 1024 * 1024;

const TemplateUploadStep = ({ data, onDataUpdate, onNext, busy }) => {
  const { t } = useTranslation();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [templateName, setTemplateName] = useState(data?.templateName || '');
  const [category, setCategory] = useState(data?.category || DocumentTemplateCategory.Contract);
  // The preview is a compact scroll box by default; HR can expand it to a full-height view to
  // verify a complete A4 certificate without scrolling a tiny window.
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const previewRef = useRef(null);

  // After the preview HTML is injected, run the render-time geometry pass (center full-bleed
  // header/footer letterheads + behind-text watermarks) in THIS display context — the exact same
  // pass htmlToPdf runs before the PDF capture — so the preview and the downloaded PDF stay
  // pixel-identical (WYSIWYG) and both match Word.
  useEffect(() => {
    if (!data?.templatePreview || !previewRef.current) return;
    const el = previewRef.current;
    const run = () => applyDocxLayoutFixes(el);
    const raf = requestAnimationFrame(() => requestAnimationFrame(run));
    if (document.fonts?.ready) document.fonts.ready.then(run).catch(() => {});
    return () => cancelAnimationFrame(raf);
  }, [data?.templatePreview]);

  const processTemplate = async (file) => {
    setIsProcessing(true);
    setError(null);
    setUploadProgress(30);

    try {
      if (file.size > MAX_FILE_BYTES) {
        setError(t('File size exceeds the maximum allowed size of 10 MB'));
        setIsProcessing(false);
        setUploadProgress(0);
        return;
      }

      const arrayBuffer = await file?.arrayBuffer();
      setUploadProgress(50);

      // High-fidelity DOCX → HTML via docx-preview: self-contained HTML (scoped <style> +
      // base64 images) that mirrors the Word document's appearance, plus the detected
      // {{placeholder}} names. Replaces mammoth, which discarded visual formatting.
      const { html, detectedFields } = await convertDocxToHtml(arrayBuffer);
      setUploadProgress(100);

      onDataUpdate('uploadedTemplate', file);
      onDataUpdate('fileIsNew', true);
      onDataUpdate('originalFileName', file?.name);
      onDataUpdate('templatePreview', html);
      onDataUpdate('detectedFields', detectedFields);
      onDataUpdate('templateName', templateName || file?.name);
      onDataUpdate('category', category);

      setTimeout(() => {
        setIsProcessing(false);
        setUploadProgress(0);
      }, 500);
    } catch (err) {
      console.error('Template processing error:', err);
      setError(t("Failed to process template. Please ensure it's a valid Word document."));
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles?.length > 0) {
      await processTemplate(acceptedFiles?.[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateName, category]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 1,
    disabled: isProcessing,
  });

  // Clear the staged file so a different .docx can be chosen. templateId is kept on
  // purpose: when editing a saved template, the next upload is routed through the
  // replace-file path (fileIsNew is re-set to true when a new file is processed) rather
  // than silently dropping the change.
  const handleRemoveTemplate = () => {
    onDataUpdate('uploadedTemplate', null);
    onDataUpdate('fileIsNew', false);
    onDataUpdate('originalFileName', '');
    onDataUpdate('templatePreview', null);
    onDataUpdate('detectedFields', []);
    onDataUpdate('fieldMappings', []);
    setError(null);
  };

  // A document must be staged before proceeding: either a freshly uploaded file, or the
  // stored HTML of a saved template loaded for editing. Gating on this (not just templateId)
  // means clicking "Replace" — which clears the preview AND mappings — disables Continue
  // until a new file is processed, so an edit can never silently save empty mappings.
  const hasTemplate = data?.uploadedTemplate || data?.templatePreview;

  const handleNext = () => {
    if (hasTemplate && templateName?.trim()) {
      onDataUpdate('templateName', templateName.trim());
      onDataUpdate('category', category);
      onNext();
    }
  };

  const canProceed = hasTemplate && templateName?.trim()?.length > 0;

  return (
    <div className="p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{t('Upload Document Template')}</h2>
        <p className="text-muted-foreground">
          {t('Upload a Word document (.docx) with field placeholders in the format')}{' '}
          <code className="bg-muted px-2 py-1 rounded text-sm">{'{{field_name}}'}</code>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Input
          label={t('Template Name')}
          placeholder={t('e.g., Employment Contract, Salary Certificate')}
          value={templateName}
          onChange={(e) => {
            // Push to parent state on every keystroke (like Category/Description) so the
            // create closure never reads a stale name when Continue is clicked.
            setTemplateName(e?.target?.value);
            onDataUpdate('templateName', e?.target?.value);
          }}
          required
          description={t('Give your template a descriptive name')}
        />
        <Select
          label={t('Category')}
          value={category}
          onChange={(value) => {
            setCategory(value);
            onDataUpdate('category', value);
          }}
          options={CATEGORY_OPTIONS.map((c) => ({ value: c.value, label: t(c.label) }))}
        />
        <div className="md:col-span-2">
          <Input
            label={t('Description')}
            placeholder={t('Optional description')}
            value={data?.description || ''}
            onChange={(e) => onDataUpdate('description', e?.target?.value)}
            description={t('Shown to employees browsing this template')}
          />
        </div>
      </div>

      {!hasTemplate ? (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-12 text-center transition-all cursor-pointer ${
            isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60 hover:bg-muted/40'
          } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Icon name="Upload" size={32} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {isDragActive ? t('Drop your template here') : t('Upload Word Template')}
            </h3>
            <p className="text-muted-foreground mb-4">{t('Drag and drop your .docx file here, or click to browse')}</p>
            <Button variant="outline" disabled={isProcessing}>
              {isProcessing ? t('Processing...') : t('Select File')}
            </Button>
          </div>

          {isProcessing && (
            <div className="mt-6">
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-primary h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {t('Processing template...')} {uploadProgress}%
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="border border-border rounded-lg p-6 bg-muted/30">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center me-4">
                <Icon name="FileCheck" size={24} className="text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">{data?.uploadedTemplate?.name || data?.originalFileName || t('Saved template')}</h4>
                <p className="text-sm text-muted-foreground">
                  {data?.uploadedTemplate
                    ? `${(data?.uploadedTemplate?.size / 1024)?.toFixed(2)} KB`
                    : t('Saved template — upload a new file to replace it')}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" iconName="Trash2" onClick={handleRemoveTemplate}>
              {data?.uploadedTemplate ? t('Remove') : t('Replace')}
            </Button>
          </div>

          {data?.detectedFields && data?.detectedFields?.length > 0 && (
            <div className="mt-4 p-4 bg-card rounded-lg border border-border">
              <h5 className="font-medium text-foreground mb-2 flex items-center">
                <Icon name="Search" size={16} className="me-2" />
                {t('Detected Field Placeholders')} ({data?.detectedFields?.length})
              </h5>
              <div className="flex flex-wrap gap-2">
                {data?.detectedFields?.map((field, index) => (
                  <span key={index} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                    {'{{' + field + '}}'}
                  </span>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-3">
                {t('These placeholders will be replaced with actual employee data in the next step.')}
              </p>
            </div>
          )}

          {data?.templatePreview && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium text-foreground">{t('Template Preview')}</h5>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName={previewExpanded ? 'Minimize2' : 'Maximize2'}
                  onClick={() => setPreviewExpanded((v) => !v)}
                  aria-expanded={previewExpanded}
                >
                  {previewExpanded ? t('Collapse') : t('Expand')}
                </Button>
              </div>
              {/* Native page mode renders a real A4 (794px) `section.docx` box; dir="auto" mirrors
                  the PDF capture host (htmlToPdf.ts) so bidi + the render-time geometry pass match. */}
              <div className={`bg-card rounded-lg border border-border overflow-auto p-4 ${previewExpanded ? 'max-h-[80vh]' : 'max-h-64'}`}>
                <div
                  ref={previewRef}
                  dir="auto"
                  dangerouslySetInnerHTML={{ __html: sanitizeDocumentHtml(data?.templatePreview || '') }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <Icon name="AlertCircle" size={20} className="text-red-600 me-3 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="font-medium text-red-900 mb-1">{t('Upload Error')}</h5>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h5 className="font-medium text-blue-900 mb-2 flex items-center">
          <Icon name="Info" size={16} className="me-2" />
          {t('Template Guidelines')}
        </h5>
        <ul className="text-sm text-blue-800 space-y-1 ms-6 list-disc">
          <li>
            {t('Use')} <code className="bg-blue-100 px-1 rounded">{'{{field_name}}'}</code> {t('format for placeholders')}
          </li>
          <li>
            {t('Example:')} <code className="bg-blue-100 px-1 rounded">{'{{employee_name}}'}</code>,{' '}
            <code className="bg-blue-100 px-1 rounded">{'{{salary_in_words_en}}'}</code>,{' '}
            <code className="bg-blue-100 px-1 rounded">{'{{company_cr_number}}'}</code>
          </li>
          <li>
            {t('Employee, company, and general fields are available, plus your HR-defined custom fields as')}{' '}
            <code className="bg-blue-100 px-1 rounded">{'{{custom_...}}'}</code>
          </li>
          <li>{t('Field names should be descriptive and use underscores for spaces')}</li>
          <li>{t('Maximum file size: 10 MB')}</li>
        </ul>
      </div>

      <div className="mt-8 flex justify-end">
        <Button onClick={handleNext} disabled={!canProceed || busy} loading={busy}>
          {t('Continue to Field Mapping')}
          <Icon name="ArrowRight" size={16} className="ms-2 rtl:rotate-180" />
        </Button>
      </div>
    </div>
  );
};

export default TemplateUploadStep;
